import { Service } from "../types";

const STATUS_PRIORITY: Record<string, number> = {
  "building":     0,
  "failed":       1,
  "delete-stuck": 2,
  "degraded":     3,
  "deleting":     4,
  "pending":      5,
  "stopped":      6,
  "sleeping":     7,
  "running":      8,
  "archived":     9,
};

/** Worst-wins composite status across a set of services. */
export function getCompositeStatus(services: Service[]): string {
  if (services.length === 0) return "running";
  let worstStatus = "running";
  let worstPriority = STATUS_PRIORITY["running"] ?? 99;
  for (const svc of services) {
    const p = STATUS_PRIORITY[svc.status] ?? 99;
    if (p < worstPriority) {
      worstPriority = p;
      worstStatus = svc.status;
    }
  }
  return worstStatus;
}

export interface StackEntry {
  primary: Service;
  dependencies: Service[];
}

export interface GroupedServices {
  standalone: Service[];
  stacks: Record<string, StackEntry>;
}

/**
 * Splits a flat services array into standalone services and named stacks.
 * Services without a stackId go into standalone[].
 * Services sharing a stackId are grouped under stacks[stackId].
 */
export function groupServices(services: Service[]): GroupedServices {
  const standalone: Service[] = [];
  const buckets: Record<string, { primary: Service | null; dependencies: Service[] }> = {};

  for (const svc of services) {
    if (!svc.stackId) {
      standalone.push(svc);
      continue;
    }
    if (!buckets[svc.stackId]) {
      buckets[svc.stackId] = { primary: null, dependencies: [] };
    }
    if (svc.stackRole === "primary") {
      buckets[svc.stackId].primary = svc;
    } else {
      buckets[svc.stackId].dependencies.push(svc);
    }
  }

  const stacks: Record<string, StackEntry> = {};
  for (const stackId of Object.keys(buckets)) {
    const { primary, dependencies } = buckets[stackId];
    const resolvedPrimary = primary ?? dependencies[0];
    const resolvedDeps = primary ? dependencies : dependencies.slice(1);
    if (resolvedPrimary) {
      stacks[stackId] = { primary: resolvedPrimary, dependencies: resolvedDeps };
    }
  }

  return { standalone, stacks };
}
